import { memo, useCallback, useMemo, useState } from "react";

import { useProductReviews } from "@react/hooks/useProductReviews";
import { useProductReviewsAggregate } from "@react/hooks/useProductReviewsAggregate";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type ProductReviewsSettings,
  type ReviewsProductData,
} from "@root/tablescape/state";

import {
  type ReviewSummary,
  type Reviews,
  ProductReviewsBaseView,
} from "./ProductReviewsBase.view";

const ProductReviewsBaseBase = () => {
  const {
    products,
    settings: { theme },
  } = useSectionSettings<ProductReviewsSettings>();

  const [currentProduct, setCurrentProduct] = useState<ReviewsProductData>(
    products[0]
  );

  const productIds = useMemo<string[]>(
    () => products.map((product) => product.id),
    [products]
  );

  const initialPageByProductId = useMemo<Record<string, number>>(() => {
    const pages: Record<string, number> = {};
    productIds.forEach((productId) => {
      pages[productId] = 1;
    });
    return pages;
  }, [productIds]);

  const [pageByProductId, setPageByProductId] = useState<
    Record<string, number>
  >(initialPageByProductId);

  const {
    aggregateReviewCount,
    aggregateReviewScore,
    aggregateStarDistribution,
    reviewsAggregateIsLoading,
  } = useProductReviewsAggregate(productIds);

  const reviewSummary = useMemo<ReviewSummary>(
    () => ({
      count: aggregateReviewCount,
      distribution: aggregateStarDistribution,
      score: aggregateReviewScore,
    }),
    [aggregateReviewCount, aggregateReviewScore, aggregateStarDistribution]
  );

  const { groupingData, pagination, reviews, reviewsAreLoading } =
    useProductReviews(currentProduct.id, pageByProductId[currentProduct.id]);

  const reviewsData = useMemo<Reviews>(
    () => ({
      items: reviews,
      pagination,
    }),
    [reviews, pagination]
  );

  const isAggregate = useMemo(() => productIds.length > 1, [productIds]);

  const productNames = useMemo(
    () => products.map((product) => product.name),
    [products]
  );

  const handleTabClick = useCallback(
    (index: number) => {
      const selectedProduct = products[index];
      if (selectedProduct) {
        setCurrentProduct(selectedProduct);
      }
    },
    [products]
  );

  const handlePageChange = useCallback(
    (page: number) =>
      setPageByProductId((prevState) => ({
        ...prevState,
        [currentProduct.id]: page,
      })),
    [currentProduct.id]
  );

  return (
    <ProductReviewsBaseView
      currentProduct={currentProduct}
      groupingData={groupingData}
      handlePageChange={handlePageChange}
      handleTabClick={handleTabClick}
      isAggregate={isAggregate}
      reviewSummary={reviewSummary}
      reviewSummaryIsLoading={reviewsAggregateIsLoading}
      reviews={reviewsData}
      reviewsAreLoading={reviewsAreLoading}
      tabLabels={productNames}
      theme={theme}
    />
  );
};

export const ProductReviewsBase = memo(ProductReviewsBaseBase);
